import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Shield } from 'lucide-react';

export default function PrivacyPolicy() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900">
      {/* Header */}
      <header className="fixed top-0 w-full z-50 bg-black/80 backdrop-blur-md border-b border-gold/20">
        <div className="container mx-auto px-6 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-300 hover:text-gold transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l'accueil
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="pt-24 pb-16 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Hero */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full mb-4">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Politique de Confidentialité
            </h1>
            <p className="text-gray-400">
              Dernière mise à jour : {new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          </div>

          {/* Content */}
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl p-8 border border-gray-700 space-y-8 text-gray-300">
            
            <section>
              <h2 className="text-2xl font-bold text-white mb-4">1. Introduction</h2>
              <p className="mb-4">
                Luxury Transfer Service (ci-après "nous", "notre" ou "le Service") s'engage à protéger la confidentialité 
                et la sécurité de vos données personnelles. Cette politique de confidentialité explique comment nous collectons, 
                utilisons, stockons et protégeons vos informations conformément au Règlement Général sur la Protection des Données (RGPD).
              </p>
              <p>
                <strong>Responsable du traitement :</strong><br />
                Luxury Transfer Service<br />
                Email : contact@luxurytransferservices.be<br />
                Téléphone : +32 2 315 02 54
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">2. Données Collectées</h2>
              <p className="mb-3">Nous collectons les données suivantes :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Données d'identification</strong> : Nom, prénom, email, numéro de téléphone</li>
                <li><strong>Données de réservation</strong> : Adresses de prise en charge et de destination, date, heure, type de service</li>
                <li><strong>Données de connexion</strong> : Adresse IP, cookies de session, tokens d'authentification</li>
                <li><strong>Données de fidélité</strong> : Historique des réservations, points fidélité</li>
                <li><strong>Données de paiement</strong> : Informations de facturation (traitées par nos partenaires de paiement sécurisés)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">3. Base Légale et Finalités du Traitement</h2>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-amber-500 mb-2">Exécution du contrat</h3>
                  <p>Traitement de vos réservations, communication sur votre service, facturation</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-amber-500 mb-2">Consentement</h3>
                  <p>Newsletter marketing, programme de fidélité, cookies non-essentiels</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-amber-500 mb-2">Intérêt légitime</h3>
                  <p>Amélioration de nos services, sécurité, prévention de la fraude</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-amber-500 mb-2">Obligations légales</h3>
                  <p>Conservation des données fiscales et comptables</p>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">4. Durée de Conservation</h2>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Données de réservation :</strong> 3 ans après la dernière prestation</li>
                <li><strong>Données comptables :</strong> 10 ans (obligation légale)</li>
                <li><strong>Compte client inactif :</strong> Suppression après 3 ans sans connexion</li>
                <li><strong>Cookies :</strong> Maximum 13 mois</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">5. Destinataires des Données</h2>
              <p className="mb-3">Vos données peuvent être partagées avec :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Chauffeurs partenaires :</strong> Uniquement les informations nécessaires à la prestation</li>
                <li><strong>Prestataires techniques :</strong> Hébergement (sécurisé), service email, paiement</li>
                <li><strong>Autorités :</strong> Sur réquisition judiciaire uniquement</li>
              </ul>
              <p className="mt-4 text-sm text-gray-400">
                ⚠️ Nous ne vendons jamais vos données à des tiers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">6. Vos Droits RGPD</h2>
              <p className="mb-3">Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Droit d'accès :</strong> Obtenir une copie de vos données</li>
                <li><strong>Droit de rectification :</strong> Corriger des données inexactes</li>
                <li><strong>Droit à l'effacement :</strong> Supprimer vos données (sous conditions)</li>
                <li><strong>Droit à la portabilité :</strong> Recevoir vos données dans un format structuré</li>
                <li><strong>Droit d'opposition :</strong> Vous opposer au traitement de vos données</li>
                <li><strong>Droit de limitation :</strong> Demander la limitation du traitement</li>
                <li><strong>Droit de retrait du consentement :</strong> À tout moment</li>
              </ul>
              <p className="mt-4">
                <strong>Pour exercer vos droits :</strong><br />
                Email : <a href="mailto:contact@luxurytransferservices.be" className="text-amber-500 hover:text-amber-400">contact@luxurytransferservices.be</a><br />
                Téléphone : +32 2 315 02 54<br />
                <span className="text-sm text-gray-400">Réponse sous 1 mois maximum</span>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">7. Sécurité des Données</h2>
              <p className="mb-3">Nous mettons en œuvre des mesures de sécurité strictes :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Chiffrement SSL/TLS pour toutes les communications</li>
                <li>Authentification sécurisée (Magic Link sans mot de passe)</li>
                <li>Serveurs hébergés dans l'UE avec certifications ISO 27001</li>
                <li>Sauvegardes régulières et cryptées</li>
                <li>Accès limité aux données par authentification forte</li>
                <li>Surveillance continue des intrusions</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">8. Cookies</h2>
              <p className="mb-3">Nous utilisons des cookies pour :</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li><strong>Cookies essentiels :</strong> Authentification, session (pas de consentement requis)</li>
                <li><strong>Cookies analytiques :</strong> Amélioration de l'expérience (avec consentement)</li>
              </ul>
              <p className="mt-4 text-sm">
                Vous pouvez gérer vos préférences cookies dans les paramètres de votre navigateur.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">9. Transferts Hors UE</h2>
              <p>
                Vos données sont stockées et traitées au sein de l'Union Européenne. 
                En cas de transfert hors UE (prestataires tiers), nous garantissons un niveau de protection adéquat 
                via les clauses contractuelles types de la Commission Européenne.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">10. Modifications</h2>
              <p>
                Cette politique peut être mise à jour. Toute modification substantielle vous sera notifiée par email. 
                La version en vigueur est toujours disponible sur cette page avec sa date de mise à jour.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">11. Réclamation</h2>
              <p>
                Si vous estimez que vos droits ne sont pas respectés, vous pouvez introduire une réclamation auprès de 
                l'Autorité de Protection des Données (APD) en Belgique :
              </p>
              <p className="mt-3">
                <strong>Autorité de Protection des Données</strong><br />
                Rue de la Presse, 35<br />
                1000 Bruxelles<br />
                Email : contact@apd-gba.be<br />
                Site : <a href="https://www.autoriteprotectiondonnees.be" target="_blank" rel="noopener noreferrer" className="text-amber-500 hover:text-amber-400">www.autoriteprotectiondonnees.be</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-white mb-4">12. Contact</h2>
              <p>
                Pour toute question concernant cette politique de confidentialité ou vos données personnelles, 
                contactez-nous :
              </p>
              <div className="mt-4 bg-gray-900/50 p-4 rounded-lg">
                <p><strong>Luxury Transfer Service</strong></p>
                <p>Email : <a href="mailto:contact@luxurytransferservices.be" className="text-amber-500 hover:text-amber-400">contact@luxurytransferservices.be</a></p>
                <p>Téléphone : +32 2 315 02 54</p>
                <p className="text-sm text-gray-400 mt-2">Disponible 24/7</p>
              </div>
            </section>

          </div>
        </div>
      </div>
    </div>
  );
}
